import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { User } from '../../types';
import { UserPlus, Edit, Trash2, Shield, Lock, Mail, Briefcase } from 'lucide-react';
import UserModal from '../UserModal';
import TwoFactorSetupModal from '../TwoFactorSetupModal';
import NegociateurSyncPanel from './NegociateurSyncPanel';
import AssistanteSyncPanel from './AssistanteSyncPanel';

export default function UserSettings() {
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [is2FAModalOpen, setIs2FAModalOpen] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    const { data, error } = await supabase
      .from('users')
      .select('id, email, firstname, lastname, agency, role, fonction, totp_enabled, email_notifications_enabled, email_2fa_enabled');
    
    if (error) {
      console.error('Error loading users:', error);
      return;
    }

    setUsers(data || []);
  };

  const handleEditClick = (user: User) => {
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  const handleAddClick = () => {
    setSelectedUser(null);
    setIsModalOpen(true);
  };

  const handleDeleteUser = async (user: User) => {
    if (!confirm(`Êtes-vous sûr de vouloir supprimer l'utilisateur ${user.firstname} ${user.lastname} ?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('users')
        .delete()
        .eq('id', user.id);

      if (error) throw error;
      loadUsers();
    } catch (err: any) {
      setError(`Erreur lors de la suppression : ${err.message}`);
    }
  };

  const handle2FASetup = (user: User) => {
    setSelectedUser(user);
    setIs2FAModalOpen(true);
  };

  const handleDisable2FA = async (user: User) => {
    if (!confirm('Êtes-vous sûr de vouloir désactiver la double authentification pour cet utilisateur ?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('users')
        .update({
          totp_secret: null,
          totp_enabled: false,
          totp_verified: false
        })
        .eq('id', user.id);

      if (error) throw error;
      loadUsers();
    } catch (err: any) {
      setError(`Erreur lors de la désactivation de la 2FA : ${err.message}`);
    }
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-purple-100 text-purple-800';
      case 'direction':
        return 'bg-amber-100 text-amber-800';
      case 'responsable':
        return 'bg-blue-100 text-blue-800';
      case 'lecture':
        return 'bg-slate-100 text-slate-700';
      default:
        return 'bg-green-100 text-green-800';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin':      return 'Administrateur';
      case 'direction':  return 'Direction';
      case 'responsable': return 'Responsable';
      case 'lecture':    return 'Lecture seule';
      default:           return 'Utilisateur';
    }
  };

  const getFonctionBadge = (fonction: string | null | undefined) => {
    switch (fonction) {
      case 'negociateur':
        return { label: 'Négociateur', cls: 'bg-emerald-50 text-emerald-700 border border-emerald-200' };
      case 'assistante':
        return { label: 'Assistante', cls: 'bg-violet-50 text-violet-700 border border-violet-200' };
      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">

      {/* Panneaux de synchronisation → comptes utilisateurs */}
      <NegociateurSyncPanel  onAccountCreated={loadUsers} />
      <AssistanteSyncPanel   onAccountCreated={loadUsers} />

      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          <span className="font-semibold text-slate-700">{users.length}</span> utilisateur{users.length !== 1 ? 's' : ''}
        </p>
        <button
          onClick={handleAddClick}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 text-sm font-semibold transition-colors shadow-sm"
        >
          <UserPlus size={16} />
          <span>Ajouter un utilisateur</span>
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-700 text-sm">{error}</div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Nom complet</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Email</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Agence</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Fonction</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Rôle</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">2FA</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Notifications Email</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {users.map((user) => (
                <tr key={user.id} className="border-b border-slate-100 last:border-0 hover:bg-indigo-50/30 transition-colors">
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-slate-800">
                    {user.firstname} {user.lastname}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-500">{user.email}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1 max-w-xs">
                      {(user.agency || '').split(',').map(a => a.trim()).filter(Boolean).map(a => (
                        <span key={a} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-md text-xs font-medium whitespace-nowrap">
                          {a}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {(() => {
                      const fb = getFonctionBadge((user as any).fonction);
                      return fb ? (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${fb.cls}`}>
                          <Briefcase size={10} />
                          {fb.label}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`px-2 py-0.5 inline-flex text-xs font-semibold rounded-full ${getRoleBadgeClass(user.role)}`}>
                      {getRoleLabel(user.role)}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex flex-col gap-1.5">
                      {/* TOTP — admins seulement */}
                      {user.role === 'admin' && (
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                            user.totp_enabled ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'
                          }`}>
                            <Shield size={10} />
                            {user.totp_enabled ? 'Authenticator' : 'Authenticator off'}
                          </span>
                          {user.totp_enabled ? (
                            <button
                              onClick={() => handleDisable2FA(user)}
                              className="p-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 transition-colors"
                              title="Désactiver la double authentification"
                            >
                              <Lock size={14} />
                            </button>
                          ) : (
                            <button
                              onClick={() => handle2FASetup(user)}
                              className="p-1.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-600 transition-colors"
                              title="Configurer la double authentification"
                            >
                              <Shield size={14} />
                            </button>
                          )}
                        </div>
                      )}
                      {/* Email 2FA — tous les rôles */}
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                        user.email_2fa_enabled ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-500'
                      }`}>
                        <Mail size={10} />
                        {user.email_2fa_enabled ? '2FA email activé' : '2FA email off'}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                      user.email_notifications_enabled ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'
                    }`}>
                      {user.email_notifications_enabled ? 'Activées' : 'Désactivées'}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => handleEditClick(user)}
                        className="p-1.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-600 transition-colors"
                        title="Modifier l'utilisateur"
                      >
                        <Edit size={15} />
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user)}
                        className="p-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 transition-colors"
                        title="Supprimer l'utilisateur"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <UserModal
        user={selectedUser}
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setSelectedUser(null); }}
        onSuccess={loadUsers}
      />

      <TwoFactorSetupModal
        isOpen={is2FAModalOpen}
        onClose={() => { setIs2FAModalOpen(false); setSelectedUser(null); }}
        onSuccess={loadUsers}
      />
    </div>
  );
}